import styled, { keyframes } from "styled-components";

const rise = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`;

const ring = keyframes`
  to { transform: rotate(360deg); }
`;

export const HeroContainer = styled.section`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  padding: 132px 28px 48px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 110px 18px 36px;
  }
`;

export const HeroInnerContainer = styled.div`
  width: 100%;
  max-width: 1120px;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 48px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

export const HeroLeftContainer = styled.div`
  animation: ${rise} 0.8s ease both;
`;

export const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 8px;
  border-radius: 999px;
  border: 1px solid rgba(62, 224, 197, 0.35);
  background: rgba(62, 224, 197, 0.08);
  color: ${({ theme }) => theme.primary};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 18px;
`;

export const LiveDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  animation: pulse-dot 1.8s infinite;
`;

export const Title = styled.h1`
  font-family: "Syne", sans-serif;
  font-weight: 800;
  font-size: clamp(42px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: ${({ theme }) => theme.text_primary};
`;

export const TextLoop = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: clamp(20px, 2.4vw, 28px);
  font-weight: 700;
  color: ${({ theme }) => theme.text_secondary};
  min-height: 40px;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

export const Span = styled.span`
  color: ${({ theme }) => theme.accent};
`;

export const SubTitle = styled.p`
  margin-top: 18px;
  max-width: 560px;
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_secondary};

  @media (max-width: 960px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const ButtonRow = styled.div`
  margin-top: 28px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

export const ResumeButton = styled.a`
  text-decoration: none;
  padding: 14px 22px;
  border-radius: 999px;
  font-weight: 800;
  color: #07110f;
  background: linear-gradient(120deg, ${({ theme }) => theme.primary}, #8ff6e4);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(62, 224, 197, 0.28);
  }
`;

export const GhostButton = styled.a`
  text-decoration: none;
  padding: 14px 22px;
  border-radius: 999px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.03);
  transition: transform 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const Meta = styled.div`
  margin-top: 22px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;

  a {
    color: ${({ theme }) => theme.text_primary};
    text-decoration: none;
    border-bottom: 1px solid rgba(62, 224, 197, 0.45);
  }

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

export const HeroRightContainer = styled.div`
  display: flex;
  justify-content: center;
  animation: ${rise} 0.9s ease 0.1s both;
`;

export const PortraitFrame = styled.div`
  position: relative;
  width: min(380px, 86vw);
  height: min(380px, 86vw);
  display: grid;
  place-items: center;
  animation: ${float} 6s ease-in-out infinite;
`;

export const Ring = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px dashed rgba(245, 185, 66, 0.7);
  animation: ${ring} 22s linear infinite;
`;

export const RingInner = styled.div`
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  border: 1.5px solid rgba(62, 224, 197, 0.55);
`;

export const Img = styled.img`
  width: 78%;
  height: 78%;
  object-fit: cover;
  border-radius: 50%;
  position: relative;
  z-index: 1;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
`;

export const TickerWrap = styled.div`
  margin-top: 54px;
  width: 100%;
  max-width: 1120px;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
`;

export const TickerTrack = styled.div`
  display: flex;
  width: max-content;
  gap: 28px;
  animation: ticker 28s linear infinite;
  color: ${({ theme }) => theme.text_secondary};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 13px;
`;
